"use strict";

var _interopRequireDefault = require("@babel/runtime/helpers/interopRequireDefault")["default"];
Object.defineProperty(exports, "__esModule", {
  value: true
});
exports["default"] = void 0;
exports.getPixelmonInstance = getPixelmonInstance;
var _regeneratorRuntime2 = _interopRequireDefault(require("@babel/runtime/helpers/esm/regeneratorRuntime"));
var _asyncToGenerator2 = _interopRequireDefault(require("@babel/runtime/helpers/esm/asyncToGenerator"));
var _toConsumableArray2 = _interopRequireDefault(require("@babel/runtime/helpers/esm/toConsumableArray"));
var _classCallCheck2 = _interopRequireDefault(require("@babel/runtime/helpers/esm/classCallCheck"));
var _createClass2 = _interopRequireDefault(require("@babel/runtime/helpers/esm/createClass"));
var _getLogger = _interopRequireDefault(require("../getLogger"));
var _insertTemplateIntoDocument = _interopRequireDefault(require("./insertTemplateIntoDocument"));
var _renderTemplate = _interopRequireDefault(require("./renderTemplate"));
var _eventLite = _interopRequireDefault(require("event-lite"));
var _presetFacebookServerSidePixelConfig = _interopRequireDefault(require("./presetFacebookServerSidePixelConfig"));
var logger = (0, _getLogger["default"])('pixelmon', 'warn');

/**
* @typedef {object} PixelConfig
* @property {string} id The CMS (contentful) unique id of the pixel
* @property {string} name The human-readable name of the pixel
* @property {string} platform Who the data is sent to e.g. Facebook, Snapchat, Google Analytics
* @property {string} requestingPartner Who asked for the pixel to be fired  e.g. 'Paretto"
* @property {string} event The player event that will trigger this pixel to be fired
* @property {object} htmlTemplate The HTML to be added to the document for this "pixel" - this will usually be e.g. a `<script>` tag. The template supports handlebars-style {{object.subobject.subproperty}} injection, where the context is the player's redux store with an additional `arguments` object that represents the arguments passed to the event handlerq.
* @property {Boolean} live Whether the pixel was marked "live" in contentful when the pixel was fired. Otherwise, it's likely QA traffic
*/

/**
* @typedef {object} PixelRecord
* @property {number} timestamp Date.now() when the pixel is fired
* @property {PixelConfig} pixelConfig The PixelConfig of the pixel that was fired
* @property {String} renderedHtmlTemplate The final htmlTemplate, rendered with the context passed when the object was fired
* @property {Object} context The context object passed to the track() call that caused this pixel to be fired
*/

/**
 * A Pixelmon pixel manager
 * @class Pixelmon
 *
 * @extends EventLite
 */
var Pixelmon = /*#__PURE__*/function () {
  /**
  *Creates an instance of Pixelmon.
  * @memberof Pixelmon
  */
  function Pixelmon() {
    (0, _classCallCheck2["default"])(this, Pixelmon);
    this._activePixelEvents = [];
    this._viewHistory = {
      firedPixelConfigIds: new Set() // A Set of config ids that have been fired so far
    };
    this._sessionHistory = {
      firedPixels: []
    };
    this.registeredPixels = false;
    this.preRegistrationQueuedTrackCalls = [];
    this.track = this.track.bind(this);
    this.registerPixels = this.registerPixels.bind(this);
    this._eventEmitter = new _eventLite["default"]();
  }

  /**
  * Register an array of pixels to fire when the relevant event is track()'d.
  *
  * @param {Array<PixelConfig>} pixelConfigs The pixel configs
  * @memberof Pixelmon
  */
  return (0, _createClass2["default"])(Pixelmon, [{
    key: "registerPixels",
    value: function registerPixels(pixelConfigs) {
      var _this = this;
      pixelConfigs.forEach(function (pixelConfig) {
        logger.debug('[registerPixel]::', pixelConfig.name);

        // TODO handle duplicates?
        var eventToListenTo = pixelConfig.event;
        if (!eventToListenTo) {
          logger.warn('No valid event found. Cannot listen to event for pixels.', pixelConfig);
          return;
        }

        // We are listening to it now, so let's remember it!
        _this._activePixelEvents.push(pixelConfig);
      });
      this.registeredPixels = true;
      while (this.preRegistrationQueuedTrackCalls.length) {
        // Use shift() instead of pop() because we want to retain the order in which they were track()ed
        this.track.apply(this, (0, _toConsumableArray2["default"])(this.preRegistrationQueuedTrackCalls.shift()));
      }
      this._eventEmitter.emit('registerpixels', pixelConfigs);
    }

    /**
     * Notify pixelmon that an event has taken place - Pixelmon will fire pixels matching this event with context.
     *
     * @param {String} eventName The event being tracked
     * @param {Object} context The context that will be used to populate htmlTemplate {{template}} string variables. If `context` contains a projectId, it will be used to match projectId configs
     * @returns {Promise} A promise that resolves when all <img>s injected by all pixels fired by this event have been loaded
     * @memberof Pixelmon
     */
  }, {
    key: "track",
    value: function track(eventName, context) {
      var _this2 = this;
      logger.debug('[track]::', eventName);
      if (!this.registeredPixels) {
        return this.preRegistrationQueuedTrackCalls.push([eventName, context]);
      }
      var relatedProjectIdInEvent = this._getProjectId(context);
      logger.debug('[relatedProjectIdInEvent]::', relatedProjectIdInEvent);
      var eligibleConfigsToFire = this._activePixelEvents.filter(function (pixelConfig) {
        return pixelConfig.event === eventName;
      }) // Filter via eventName

      // Filter via _included_ projectIds, if applicable:
      .filter(function (pixelConfig) {
        // Always eligible if `projectIds` field doesn't exist
        if (!pixelConfig.projectIds) {
          return true;
        }

        // Eligible if `projectIds` exists and this project id is in the array, otherwise ineligible
        return pixelConfig.projectIds.includes(relatedProjectIdInEvent);
      })

      // Filter out _excluded_ projectIds, if applicable:
      .filter(function (pixelConfig) {
        // Always eligible if `dontFireFromTheseProjectIds` is not specified
        if (!pixelConfig.dontFireFromTheseProjectIds) {
          return true;
        }

        // Ineligible if `dontFireFromTheseProjectIds` includes this project id
        if (pixelConfig.dontFireFromTheseProjectIds.includes(relatedProjectIdInEvent)) {
          return false;
        }

        // Eligible otherwise (this project isn't explicitly excluded via `dontFireFromTheseProjectIds`)
        return true;
      })

      // Filter out `oncePerView` pixels, if applicable:
      .filter(function (pixelConfig) {
        // Always eligible if `oncePerView` is false or not defined; there's no per-view restrictions
        if (!pixelConfig.oncePerView) {
          return true;
        }

        // Ineligible if we have `oncePerView` restriction, and this pixelConfig has been fired before
        if (_this2._viewHistory.firedPixelConfigIds.has(pixelConfig.id)) {
          return false;
        }

        // Eligible if we have `oncePerView` restriction, but this pixelConfig has not been fired before
        return true;
      });
      var allRenderedNodePromises = eligibleConfigsToFire.map(function (pixelConfig) {
        return _this2._firePixel(pixelConfig, context);
      });
      return this._makeAllRenderedImgNodesLoadedPromise(allRenderedNodePromises);
    }

    /**
     * Resets the `oncePerView` state of this view, effectibely clearing the history and allowing those pixels to get fired once (but only once) again
     *
     * @memberof Pixelmon
     */
  }, {
    key: "reportNewView",
    value: function reportNewView() {
      this._viewHistory.firedPixelConfigIds.clear(); // Clear the history
    }

    /**
     * Will call `callback()` once all the `img` nodes resolved in `allRenderedNodePromises` have been loaded.
     *
     * This method only deals with <img> nodes for now - see note in `track()` util
     * @param {Promise[]} allRenderedNodePromises An array of Promises that resolve to arrays of inserted DOM Nodes (yes, you read that right)
     * @memberof Pixelmon
     */
  }, {
    key: "_makeAllRenderedImgNodesLoadedPromise",
    value: function () {
      var _makeAllRenderedImgNodesLoadedPromise2 = (0, _asyncToGenerator2["default"])( /*#__PURE__*/(0, _regeneratorRuntime2["default"])().mark(function _callee(allRenderedNodePromises) {
        var allArraysOfRenderedNodes, imgLoadedPromises;
        return (0, _regeneratorRuntime2["default"])().wrap(function _callee$(_context) {
          while (1) switch (_context.prev = _context.next) {
            case 0:
              _context.next = 2;
              return Promise.all(allRenderedNodePromises);
            case 2:
              allArraysOfRenderedNodes = _context.sent;
              imgLoadedPromises = [];
              allArraysOfRenderedNodes.forEach(function (nodes) {
                nodes.forEach(function (node) {
                  var isImgNode = node && node.nodeName && String(node.nodeName).toLowerCase() === 'img';

                  // If it's not an <img> node, we don't care about waiting for it to load (for now)
                  if (!isImgNode) {
                    return;
                  }

                  // Knowing when an img is loaded: https://stackoverflow.com/questions/280049/how-to-create-a-javascript-callback-for-knowing-when-an-image-is-loaded

                  // If .complete is already true, the <img> is already loaded, so we don't have to wait for anything new to call the callback
                  if (isImgNode.complete === true) {
                    return;
                  }

                  // Now we know `node` is for sure an image node that hasn't loaded yet...
                  var thisImageLoadedPromise = new Promise(function (resolve) {
                    node.addEventListener('load', resolve);
                  });
                  imgLoadedPromises.push(thisImageLoadedPromise);
                });
              });

              // Wait for every img that wasn't loaded to get loaded...
              return _context.abrupt("return", Promise.all(imgLoadedPromises));
            case 6:
            case "end":
              return _context.stop();
          }
        }, _callee);
      }));
      function _makeAllRenderedImgNodesLoadedPromise(_x) {
        return _makeAllRenderedImgNodesLoadedPromise2.apply(this, arguments);
      }
      return _makeAllRenderedImgNodesLoadedPromise;
    }()
    /**
     * Borrowed from EventLite; allows you to listen to Pixelmon's events.
     *
     * Supported events:
     * - `pixel` - fired for each pixel fired from a matching .track() call, with a 'PixelRecord'
     * - `registerpixels` - fired when pixels are registered, with the array of pixelConfigs that have been registered
     * @param {String} event
     * @param {Function} handler
     * @memberof Pixelmon
     */
  }, {
    key: "on",
    value: function on(event, handler) {
      this._eventEmitter.on(event, handler);
    }
  }, {
    key: "_getProjectId",
    value: function _getProjectId(context) {
      if (context && context.projectId) {
        return context.projectId;
      }
      if (context && context.ekoId) {
        // In some apps the ekoId field represented the project
        return context.ekoId;
      }
      return this.projectId;
    }

    /**
     *
     *
     * @param {*} pixelConfig
     * @param {*} context
     * @memberof Pixelmon
     */
  }, {
    key: "_firePixel",
    value: function _firePixel(pixelConfig, context) {
      // TODO: possible contexts to add:
      // - player state?
      // - query params like utm params?
      // etc etc etc as needed by product requests

      // context projectId takes precedence
      if (!context.projectId && this.projectId) {
        context.projectId = this.projectId;
      }
      var extendedContext = Object.assign({}, context, {
        globals: {
          appId: this.appId,
          projectId: this.projectId,
          userId: this.userId,
          sessionId: this.sessionId,
          pageViewId: this.pageViewId
        }
      });

      // We store in a new object, so we don't modified the registered pixels if they are to be re-injected
      var toBeInjectedPixel = (0, _presetFacebookServerSidePixelConfig["default"])(pixelConfig, extendedContext);
      var renderedHtmlTemplate = (0, _renderTemplate["default"])(toBeInjectedPixel.htmlTemplate, extendedContext);

      // This is a promise that resolves with the array of nodes that this renderedHtmlTemplate rendered into the body
      // (It's a promise because the nodes are inserted in a different tick)
      var insertedNodesPromise = (0, _insertTemplateIntoDocument["default"])(toBeInjectedPixel.name, renderedHtmlTemplate);
      var pixelRecord = {
        timestamp: Date.now(),
        pixelConfig: toBeInjectedPixel,
        renderedHtmlTemplate: renderedHtmlTemplate,
        context: context
      };
      this._sessionHistory.firedPixels.push(pixelRecord);
      this._viewHistory.firedPixelConfigIds.add(pixelConfig.id);
      logger.debug('[_firePixel]::', pixelConfig.name);
      if (window.EkoAnalytics) {
        // We do this lazily because we might initialize the instance before window.EkoAnalytics is available,
        // and then we'd never register the packer
        this._ensureEAContextPackerRegistered();
        // eslint-disable-next-line new-cap
        window.EkoAnalytics('track', 'pixelmon.pixel', toBeInjectedPixel);
      }
      this._eventEmitter.emit('pixel', pixelRecord);
      return insertedNodesPromise;
    }
  }, {
    key: "_ensureEAContextPackerRegistered",
    value: function _ensureEAContextPackerRegistered() {
      if (this._eaContextPackerRegistered) {
        return;
      }
      if (window.EkoAnalytics) {
        // eslint-disable-next-line new-cap
        window.EkoAnalytics('registerContextPacker', {
          schema: 'iglu:com.helloeko/pixelmon_pixel/jsonschema/1-0-3',
          match: 'pixelmon.pixel',
          getData: function getData(pixelConfig) {
            var pixelData = {
              id: pixelConfig.id,
              name: pixelConfig.name,
              platform: pixelConfig.platform,
              campaign: pixelConfig.campaign,
              alias: pixelConfig.alias,
              event: pixelConfig.event,
              live: pixelConfig.live
            };
            if (pixelConfig.serverside) {
              pixelData.serverside = pixelConfig.serverside;
            }
            if (/facebook/i.test(pixelConfig.platform) && pixelConfig.facebookpixel) {
              pixelData.facebookpixel = pixelConfig.facebookpixel;
            }
            return pixelData;
          }
        });
        this._eaContextPackerRegistered = true;
      }
    }

    /**
     * Inform the pixelmon instance of the page's EkoVideo projectId
     *
     * _NOTE: This will also **clear the `oncePerView`** history if projectId is changed._
     *
     * _(This is because on long-running pages, where projects are just loaded and disposed of via modals,
     * pixelmon treats it as one very long view when the viewer closes the modal and opens a new one, so `oncePerView` pixels get disabled from previous projects.)_
     * @param projectId
     */
  }, {
    key: "_setProjectId",
    value: function _setProjectId(projectId) {
      if (projectId !== this.projectId) {
        this._viewHistory.firedPixelConfigIds.clear(); // If it's a new projectId, clear the history
      }
      this.projectId = projectId;
    }

    /**
     * Set global fields for Pixelmon which will be used for html templates injection for fields of the pattern {{globals.*}}
     * @param appId
     * @param projectId
     * @param userId
     * @param sessionId
     * @param pageViewId
     */
  }, {
    key: "setGlobals",
    value: function setGlobals(_ref) {
      var appId = _ref.appId,
        projectId = _ref.projectId,
        userId = _ref.userId,
        sessionId = _ref.sessionId,
        pageViewId = _ref.pageViewId;
      // Setting a new  projectId requires clearing the view history
      if (projectId) {
        this._setProjectId(projectId);
      }
      this.appId = appId;
      this.userId = userId;
      this.sessionId = sessionId;
      this.pageViewId = pageViewId;
    }
  }]);
}();
var pixelmonInstance;

// We're gonna make this a singleton now - having it in a class is convenient,
// but for now, we only need one per runtime (for now!)
/**
 *
 * @export
 * @returns {Pixelmon} pixelmonInstance
 */
function getPixelmonInstance() {
  if (typeof pixelmonInstance === 'undefined') {
    pixelmonInstance = new Pixelmon();
  }
  return pixelmonInstance;
}
var _default = exports["default"] = Pixelmon; // TODO - queue calls!?